import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2, RefreshCcw, AlertCircle, Clock, PlayCircle } from 'lucide-react';
import {
  listHeldEvents,
  releaseHeldEvent,
  type HeldEventSummary,
} from '../lib/api/triage.js';

/**
 * Track 4b held-events panel.
 *
 * Surfaces in-flight deep_backsource events to operators: the M&A research-
 * gate orchestration showing budget consumption, saturation progress,
 * counterparty count, and a "publish now" override button.
 *
 * Polls every 30s while events are in flight; backs off to manual refresh
 * when the list is empty.
 */
export default function HeldEventsPanel() {
  const queryClient = useQueryClient();
  const [includeTerminal, setIncludeTerminal] = useState(false);
  const [releaseReasons, setReleaseReasons] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['admin-triage-held-events', includeTerminal],
    queryFn: () => listHeldEvents(includeTerminal),
    refetchInterval: (q) =>
      (q.state.data?.events ?? []).some(
        (e) => e.state !== 'terminal',
      )
        ? 30_000
        : false,
  });

  const releaseMutation = useMutation({
    mutationFn: ({ eventId, reason }: { eventId: string; reason?: string }) =>
      releaseHeldEvent(eventId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-triage-held-events'] });
    },
  });

  const events = query.data?.events ?? [];
  const inFlight = events.filter((e) => e.state !== 'terminal');

  return (
    <section className="border border-slate-200 rounded-lg bg-surface p-4 mb-6">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">
            Held articles — research-gated events
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            M&amp;A / scheme / takeover events with article generation held
            until binding-graph saturation completes. Override with{' '}
            <span className="font-medium">Publish now</span> if business need
            requires.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={includeTerminal}
              onChange={(e) => setIncludeTerminal(e.target.checked)}
              className="rounded"
            />
            Include recently completed
          </label>
          <button
            onClick={() => query.refetch()}
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          >
            {query.isFetching ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCcw className="h-3 w-3" />
            )}
            Refresh
          </button>
        </div>
      </header>

      {query.isLoading && (
        <div className="text-xs text-slate-500 py-4">
          <Loader2 className="h-4 w-4 inline animate-spin mr-1.5" />
          Loading held events…
        </div>
      )}

      {query.isError && (
        <div className="text-xs text-red-600 py-4 inline-flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4" />
          Failed to load held events.
        </div>
      )}

      {!query.isLoading && events.length === 0 && (
        <div className="text-xs text-slate-500 py-4">
          No held events.{' '}
          {!includeTerminal && (
            <span className="text-slate-400">
              (Toggle &quot;Include recently completed&quot; to see the last 24h
              of terminal events.)
            </span>
          )}
        </div>
      )}

      {events.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {events.map((event) => (
            <HeldEventRow
              key={event.event_id}
              event={event}
              reason={releaseReasons[event.event_id] ?? ''}
              onReasonChange={(r) =>
                setReleaseReasons((prev) => ({
                  ...prev,
                  [event.event_id]: r,
                }))
              }
              onRelease={() =>
                releaseMutation.mutate({
                  eventId: event.event_id,
                  reason: releaseReasons[event.event_id] ?? undefined,
                })
              }
              isReleasing={
                releaseMutation.isPending &&
                releaseMutation.variables?.eventId === event.event_id
              }
            />
          ))}
        </ul>
      )}

      {inFlight.length > 0 && (
        <p className="text-xs text-slate-400 mt-2">
          Polling every 30s while {inFlight.length} event
          {inFlight.length > 1 ? 's are' : ' is'} in flight.
        </p>
      )}
    </section>
  );
}

interface HeldEventRowProps {
  event: HeldEventSummary;
  reason: string;
  onReasonChange: (r: string) => void;
  onRelease: () => void;
  isReleasing: boolean;
}

function HeldEventRow({
  event,
  reason,
  onReasonChange,
  onRelease,
  isReleasing,
}: HeldEventRowProps) {
  const isTerminal = event.state === 'terminal';
  const budgetPct =
    event.budget_max > 0
      ? Math.round((event.budget_used / event.budget_max) * 100)
      : 0;
  const satPct =
    event.saturation_jobs_total > 0
      ? Math.round(
          (event.saturation_jobs_done / event.saturation_jobs_total) * 100,
        )
      : 0;
  const sinceProgress =
    event.minutes_since_last_progress != null
      ? Math.round(event.minutes_since_last_progress)
      : null;

  const stateColor: Record<string, string> = {
    pending: 'bg-slate-100 text-slate-700',
    saturating: 'bg-amber-50 text-amber-800 border border-amber-200',
    terminal: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  };
  const outcomeColor: Record<string, string> = {
    completed: 'text-emerald-700',
    budget_exhausted: 'text-amber-700',
    circuit_breaker: 'text-red-700',
    operator_released: 'text-sky-700',
    failed: 'text-red-700',
  };

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                stateColor[event.state] ?? 'bg-slate-100'
              }`}
            >
              {event.state}
            </span>
            {event.gate_outcome && (
              <span
                className={`text-[10px] font-medium uppercase tracking-wider ${
                  outcomeColor[event.gate_outcome] ?? 'text-slate-700'
                }`}
              >
                · {event.gate_outcome.replaceAll('_', ' ')}
              </span>
            )}
            <span className="text-[10px] text-slate-400 uppercase">
              {event.activity_sub_type}
            </span>
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">
            {event.primary_company_code && (
              <span className="text-slate-500 mr-1.5">
                {event.primary_company_code}
              </span>
            )}
            {event.primary_announcement_title ?? event.primary_announcement_id}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2 text-xs text-slate-600">
            <ProgressMetric
              label="Budget"
              done={event.budget_used}
              total={event.budget_max}
              pct={budgetPct}
              dangerAt={90}
            />
            <ProgressMetric
              label="Saturation jobs"
              done={event.saturation_jobs_done}
              total={event.saturation_jobs_total}
              pct={satPct}
            />
            <div>
              <div className="text-[10px] text-slate-400 uppercase">
                Counterparties
              </div>
              <div className="font-medium">{event.counterparty_count}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase">
                Held articles
              </div>
              <div className="font-medium">{event.held_article_count}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              opened {format(new Date(event.created_at), 'HH:mm')}
            </span>
            {sinceProgress != null && (
              <span
                className={
                  sinceProgress > 30 ? 'text-amber-700' : ''
                }
              >
                · last progress {sinceProgress}m ago
              </span>
            )}
            {event.operator_released_by && (
              <span className="text-sky-700">
                · released by {event.operator_released_by}
              </span>
            )}
          </div>
        </div>

        {!isTerminal && (
          <div className="flex flex-col gap-1.5 items-end shrink-0">
            <input
              type="text"
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              className="text-xs border border-slate-200 rounded px-2 py-1 w-48"
            />
            <button
              onClick={onRelease}
              disabled={isReleasing}
              className="text-xs bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white rounded px-3 py-1 inline-flex items-center gap-1.5"
            >
              {isReleasing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <PlayCircle className="h-3 w-3" />
              )}
              Publish now
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

function ProgressMetric({
  label,
  done,
  total,
  pct,
  dangerAt,
}: {
  label: string;
  done: number;
  total: number;
  pct: number;
  dangerAt?: number;
}) {
  const inDanger = dangerAt != null && pct >= dangerAt;
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase">{label}</div>
      <div className="font-medium">
        {done}
        <span className="text-slate-400 mx-0.5">/</span>
        {total}
        <span className="text-slate-400 ml-1">({pct}%)</span>
      </div>
      <div className="h-1 bg-slate-100 rounded mt-1 overflow-hidden">
        <div
          className={`h-full transition-all ${
            inDanger ? 'bg-amber-500' : 'bg-sky-500'
          }`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}
